import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { SectionComponent } from '../../../components/layout/section/section.component';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { FormTracking } from '../form-tracking/form-tracking.component';
import {
  DEFAULT_TICKET,
  EEntityType,
  ERelationType,
  ETextType,
  ICmsTicketOverviewItem,
  ILayoutAssignment,
  INormalText,
  ITicket,
  LanguagePrefix,
} from '@web/dtos';
import { SortimentFormsFactory } from '../../../factory/sortiment/sortiment-forms-factory.service';
import { SortimentSectionComponent } from './sortiment-selection/sortiment-section.component';
import { CmsTicketOverviewService } from '../../../services/cms-ticket-overview/cms-ticket-overview.service';
import {
  ILayoutAssignmentFormValue,
  ISortimentForm,
  ITicketTileForm,
} from '../../../entities/sortiment-form.entity';
import { SortimentEditComponent } from './sortiment-edit/sortiment-edit.component';
import { SortimentPreviewComponent } from './sortiment-preview/sortiment-preview.component';
import { TicketTileFactoryService } from '../../../factory/ticketTile/ticket-tile-factory.service';
import { TicketStore } from '../../../services/api/tickets/ticket-store.service';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { SortLayoutAssignmentStore } from '../../../services/api/sort-layout-assignment/sort-layout-assignment-store.service';

@Component({
  selector: 'aw-sortiment-page',
  imports: [
    ButtonComponent,
    SectionComponent,
    SectionHeaderActionsComponent,
    SectionHeaderComponent,
    SectionHeadlineComponent,
    ReactiveFormsModule,
    SortimentSectionComponent,
    SortimentEditComponent,
    SortimentPreviewComponent,
  ],
  templateUrl: './sortiment-page.component.html',
  styleUrl: './sortiment-page.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SortimentPageComponent extends FormTracking<ISortimentForm> {
  public readonly cmsTicketOverviewService: CmsTicketOverviewService = inject(
    CmsTicketOverviewService
  );
  protected readonly selectedTicketId: WritableSignal<string | null> =
    signal(null);
  protected readonly selectedTicket: Signal<
    ICmsTicketOverviewItem | undefined
  > = computed((): ICmsTicketOverviewItem | undefined =>
    this.cmsTicketOverviewService
      .tickets()
      .find(
        (ticket: ICmsTicketOverviewItem): boolean =>
          ticket.id === this.selectedTicketId()
      )
  );
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly sortimentFormsFactory: SortimentFormsFactory = inject(
    SortimentFormsFactory
  );
  private readonly ticketTileFactory: TicketTileFactoryService = inject(
    TicketTileFactoryService
  );
  protected readonly placeholderTicketForm =
    this.ticketTileFactory.buildForm(undefined);
  private readonly ticketStore: TicketStore = inject(TicketStore);
  private readonly textStore: WebTextStore = inject(WebTextStore);
  private readonly sortLayoutAssignmentStore: SortLayoutAssignmentStore =
    inject(SortLayoutAssignmentStore);
  protected readonly isFormInitialized: WritableSignal<boolean> = signal(false);
  private readonly initialFormSnapshot: WritableSignal<string> = signal('');
  private readonly initialTicketSnapshots: WritableSignal<readonly string[]> =
    signal<readonly string[]>([]);
  private readonly initialTicketLayoutSnapshots: WritableSignal<
    ReadonlyMap<string, string>
  > = signal<ReadonlyMap<string, string>>(new Map());
  private initialTickets: ITicket[] = [];
  private initialButtonText: INormalText[] = [];
  private initialLayoutAssignments: ILayoutAssignmentFormValue[] = [];
  public readonly sortimentForm: FormGroup<ISortimentForm> =
    this.sortimentFormsFactory.buildForm();
  private readonly sortimentFormChange: Signal<unknown> = toSignal(
    this.sortimentForm.valueChanges,
    {
      initialValue: null,
    }
  );
  protected readonly hasChanges: Signal<boolean> = computed((): boolean => {
    this.sortimentFormChange();
    return (
      this.isFormInitialized() &&
      this.initialFormSnapshot() !==
        this.createSnapshot(this.sortimentForm.getRawValue())
    );
  });
  protected readonly editedTicketIds: Signal<ReadonlySet<string>> = computed(
    (): ReadonlySet<string> => {
      this.sortimentFormChange();

      const editedIds: Set<string> = new Set<string>();
      const initialSnapshots: readonly string[] = this.initialTicketSnapshots();
      const initialLayoutSnapshots = this.initialTicketLayoutSnapshots();
      this.cmsTicketOverviewService
        .tickets()
        .forEach((ticket: ICmsTicketOverviewItem, index: number): void => {
          const ticketForm: FormGroup<ITicketTileForm> | undefined =
            this.sortimentForm.controls.tickets.at(index);

          const ticketFormChanged =
            !!ticketForm &&
            initialSnapshots[index] !==
              this.createSnapshot(ticketForm.getRawValue());
          const layoutAssignmentsChanged =
            initialLayoutSnapshots.get(ticket.id) !==
            this.createSnapshot(
              this.getLayoutAssignmentValuesForTicket(ticket)
            );

          if (ticketFormChanged || layoutAssignmentsChanged) {
            editedIds.add(ticket.id);
          }
        });
      return editedIds;
    }
  );
  protected readonly completedLayoutTicketIds: Signal<ReadonlySet<string>> =
    computed((): ReadonlySet<string> => {
      this.sortimentFormChange();
      const assignedSortIds = new Set(
        this.sortimentForm.controls.layoutAssignments.controls
          .filter(form => form.controls.layoutId.value !== null)
          .map(form => form.controls.sortId.value)
      );

      return new Set(
        this.cmsTicketOverviewService
          .tickets()
          .filter((ticket: ICmsTicketOverviewItem): boolean =>
            ticket.sortenExterneReferenzen.every((sortId: string): boolean =>
              assignedSortIds.has(sortId)
            )
          )
          .map((ticket: ICmsTicketOverviewItem): string => ticket.id)
      );
    });
  protected readonly unassignedLayoutCount: Signal<number> = computed(
    (): number => {
      this.sortimentFormChange();
      const selectedTicket = this.selectedTicket();

      if (!selectedTicket) {
        return 0;
      }

      const assignedSortIds = new Set(
        this.sortimentForm.controls.layoutAssignments.controls
          .filter(form => form.controls.layoutId.value !== null)
          .map(form => form.controls.sortId.value)
      );

      return selectedTicket.sortenExterneReferenzen.filter(
        (sortId: string): boolean => !assignedSortIds.has(sortId)
      ).length;
    }
  );
  readonly selectedTicketForm: Signal<FormGroup<ITicketTileForm> | undefined> =
    computed((): FormGroup<ITicketTileForm> | undefined => {
      this.sortimentFormChange();
      const selectedId = this.selectedTicketId();

      const selectedIndex: number = this.cmsTicketOverviewService
        .tickets()
        .findIndex(
          (ticket: ICmsTicketOverviewItem): boolean => ticket.id === selectedId
        );

      return selectedIndex >= 0
        ? this.sortimentForm.controls.tickets.at(selectedIndex)
        : undefined;
    });

  constructor() {
    super();
    this.placeholderTicketForm.disable({ emitEvent: false });
    effect((): void => {
      const tickets = this.cmsTicketOverviewService.tickets();
      const selectedTicketId = this.selectedTicketId();

      if (
        tickets.length > 0 &&
        (!selectedTicketId ||
          (!this.cmsTicketOverviewService.isLoading() &&
            !tickets.some(ticket => ticket.id === selectedTicketId)))
      ) {
        this.selectedTicketId.set(tickets[0].id);
      }
    });

    effect((): void => {
      const overviewTickets: ICmsTicketOverviewItem[] =
        this.cmsTicketOverviewService.tickets();
      const buttonText = this.textStore.buttonText();

      if (
        this.isFormInitialized() ||
        this.cmsTicketOverviewService.isLoading() ||
        !this.cmsTicketOverviewService.hasLoaded() ||
        this.cmsTicketOverviewService.hasError() ||
        this.textStore.isLoading()
      ) {
        return;
      }

      const tickets: ITicket[] = overviewTickets.map(ticket => {
        const ticketData: ITicket = ticket.ticketData ?? {
          ...DEFAULT_TICKET,
          ticketId: ticket.name,
          serviceAreaId: ticket.serviceAreaId,
          texts: [],
          details: [],
        };
        const ticketTexts: INormalText[] = ticketData.texts ?? [];

        const hasTitle = ticketTexts.some(
          text => text.relationType === ERelationType.title
        );

        return {
          ...ticketData,
          texts: hasTitle
            ? ticketTexts
            : [...ticketTexts, ...this.generateDefaultTextForATicket(ticket)],
        };
      });
      this.initialTickets = this.deepCopyService.createDeepCopy(tickets);
      this.initialButtonText = this.deepCopyService.createDeepCopy(buttonText);
      this.initialLayoutAssignments = this.deepCopyService.createDeepCopy(
        this.buildInitialLayoutAssignments(overviewTickets)
      );
      const initializedForm: FormGroup<ISortimentForm> =
        this.createInitialForm();

      this.sortimentForm.setControl(
        'tickets',
        initializedForm.controls.tickets
      );
      this.sortimentForm.controls.buttonText.setValue(
        initializedForm.controls.buttonText.getRawValue(),
        { emitEvent: false }
      );
      this.sortimentForm.setControl(
        'layoutAssignments',
        initializedForm.controls.layoutAssignments
      );
      this.sortimentForm.markAsPristine();
      this.initFormTracking(this.sortimentForm);
      this.updateSnapshots();
      this.isFormInitialized.set(true);
    });
  }

  async save(): Promise<boolean> {
    if (!this.isFormInitialized() || this.cmsTicketOverviewService.hasError())
      return false;
    if (this.sortimentForm.invalid) {
      this.sortimentForm.markAllAsTouched();
      return false;
    }

    const tickets = this.sortimentForm.controls.tickets.controls.map(form =>
      this.ticketTileFactory.buildEntity(form)
    );
    const buttonText = this.sortimentForm.controls.buttonText.getRawValue();
    const initialAssignmentsBySortId = new Map(
      this.initialLayoutAssignments.map(assignment => [
        assignment.sortId,
        assignment,
      ])
    );
    const savedAt = new Date();
    const layoutAssignments: ILayoutAssignment[] =
      this.sortimentForm.controls.layoutAssignments.controls.flatMap(
        (form): ILayoutAssignment[] => {
          const layoutId = form.controls.layoutId.value;
          const initialAssignment = initialAssignmentsBySortId.get(
            form.controls.sortId.value
          );
          return layoutId === null
            ? []
            : [
                {
                  sortId: form.controls.sortId.value,
                  layoutId,
                  lastEditedAt:
                    initialAssignment?.layoutId === layoutId
                      ? form.controls.lastEditedAt.value
                      : savedAt,
                },
              ];
        }
      );

    await Promise.all([
      this.ticketStore.saveAll(tickets),
      this.textStore.save(buttonText),
      this.sortLayoutAssignmentStore.saveAll(layoutAssignments),
    ]);

    const savedAssignmentsBySortId = new Map(
      layoutAssignments.map(assignment => [assignment.sortId, assignment])
    );
    this.sortimentForm.controls.layoutAssignments.controls.forEach(form => {
      const savedAssignment = savedAssignmentsBySortId.get(
        form.controls.sortId.value
      );
      if (savedAssignment) {
        form.controls.lastEditedAt.setValue(savedAssignment.lastEditedAt, {
          emitEvent: false,
        });
      }
    });

    this.initialTickets = this.deepCopyService.createDeepCopy(tickets);
    this.initialButtonText = this.deepCopyService.createDeepCopy(buttonText);
    this.initialLayoutAssignments = this.deepCopyService.createDeepCopy(
      this.sortimentForm.controls.layoutAssignments.getRawValue()
    );
    this.sortimentForm.markAsPristine();
    this.initFormTracking(this.sortimentForm);
    this.updateSnapshots();
    this.cmsTicketOverviewService.reloadTickets();
    return true;
  }

  protected handleAbortChangesDialog(): void {
    const initialForm: FormGroup<ISortimentForm> = this.createInitialForm();

    this.sortimentForm.setControl('tickets', initialForm.controls.tickets);
    this.sortimentForm.controls.buttonText.setValue(
      initialForm.controls.buttonText.getRawValue()
    );
    this.sortimentForm.setControl(
      'layoutAssignments',
      initialForm.controls.layoutAssignments
    );
    this.sortimentForm.markAsPristine();
    this.sortimentForm.markAsUntouched();
    this.initFormTracking(this.sortimentForm);
    this.updateSnapshots();
  }

  protected resetSelectedTicket(): void {
    const selectedTicket = this.selectedTicket();
    const selectedIndex: number = this.cmsTicketOverviewService
      .tickets()
      .findIndex(
        (ticket: ICmsTicketOverviewItem): boolean =>
          ticket.id === this.selectedTicketId()
      );
    const initialTicket: ITicket | undefined =
      this.initialTickets[selectedIndex];

    if (selectedIndex < 0 || !initialTicket || !selectedTicket) {
      return;
    }

    this.sortimentForm.controls.tickets.setControl(
      selectedIndex,
      this.ticketTileFactory.buildForm(
        this.deepCopyService.createDeepCopy(initialTicket)
      )
    );
    const initialAssignmentsBySortId = new Map(
      this.initialLayoutAssignments.map(assignment => [
        assignment.sortId,
        assignment,
      ])
    );

    selectedTicket.sortenExterneReferenzen.forEach((sortId: string): void => {
      const assignmentForm =
        this.sortimentForm.controls.layoutAssignments.controls.find(
          form => form.controls.sortId.value === sortId
        );
      const initialAssignment = initialAssignmentsBySortId.get(sortId);

      if (assignmentForm && initialAssignment) {
        assignmentForm.controls.sortId.setValue(initialAssignment.sortId);
        assignmentForm.controls.layoutId.setValue(initialAssignment.layoutId);
        assignmentForm.controls.lastEditedAt.setValue(
          new Date(initialAssignment.lastEditedAt)
        );
        assignmentForm.markAsPristine();
      }
    });
  }

  private createInitialForm(): FormGroup<ISortimentForm> {
    return this.sortimentFormsFactory.buildForm(
      this.deepCopyService.createDeepCopy(this.initialTickets),
      this.deepCopyService.createDeepCopy(this.initialButtonText),
      this.deepCopyService.createDeepCopy(this.initialLayoutAssignments)
    );
  }

  private buildInitialLayoutAssignments(
    tickets: ICmsTicketOverviewItem[]
  ): ILayoutAssignmentFormValue[] {
    const assignmentsBySortId = new Map<string, ILayoutAssignment>();
    const sortIds = new Set<string>();

    tickets.forEach((ticket: ICmsTicketOverviewItem): void => {
      ticket.sortenExterneReferenzen.forEach((sortId: string): void => {
        sortIds.add(sortId);
      });
      ticket.layoutAssignments?.forEach(
        (assignment: ILayoutAssignment): void => {
          assignmentsBySortId.set(assignment.sortId, assignment);
        }
      );
    });

    return [...sortIds].map((sortId: string): ILayoutAssignmentFormValue => {
      const assignment = assignmentsBySortId.get(sortId);
      return {
        sortId,
        layoutId: assignment?.layoutId ?? null,
        lastEditedAt: assignment?.lastEditedAt ?? new Date(0),
      };
    });
  }

  private getLayoutAssignmentValuesForTicket(
    ticket: ICmsTicketOverviewItem
  ): ILayoutAssignmentFormValue[] {
    const sortIds = new Set(ticket.sortenExterneReferenzen);
    return this.sortimentForm.controls.layoutAssignments
      .getRawValue()
      .filter((assignment: ILayoutAssignmentFormValue): boolean =>
        sortIds.has(assignment.sortId)
      );
  }

  private generateDefaultTextForATicket(
    ticket: ICmsTicketOverviewItem
  ): INormalText[] {
    const texts: INormalText[] = [];
    texts.push({
      textType: ETextType.Web,
      entityType: EEntityType.ticket,
      relationType: ERelationType.title,
      text: {
        values: [
          {
            languagePrefix: 'DE' as LanguagePrefix,
            value: ticket.name,
          },
        ],
      },
    });
    return texts;
  }

  private updateSnapshots(): void {
    this.initialFormSnapshot.set(
      this.createSnapshot(this.sortimentForm.getRawValue())
    );
    this.initialTicketSnapshots.set(
      this.sortimentForm.controls.tickets.controls.map(
        (ticketForm: FormGroup<ITicketTileForm>): string =>
          this.createSnapshot(ticketForm.getRawValue())
      )
    );
    this.initialTicketLayoutSnapshots.set(
      new Map(
        this.cmsTicketOverviewService
          .tickets()
          .map((ticket: ICmsTicketOverviewItem): [string, string] => [
            ticket.id,
            this.createSnapshot(
              this.getLayoutAssignmentValuesForTicket(ticket)
            ),
          ])
      )
    );
  }

  private createSnapshot(value: unknown): string {
    return JSON.stringify(this.normalizeComparisonValue(value));
  }

  private normalizeComparisonValue(value: unknown): unknown {
    if (value instanceof Date) {
      return value.toISOString();
    }
    if (typeof value === 'string') {
      return this.normalizeQuillValue(value);
    }
    if (Array.isArray(value)) {
      return value.map((item: unknown): unknown =>
        this.normalizeComparisonValue(item)
      );
    }
    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(
          ([key, item]: [string, unknown]): [string, unknown] => [
            key,
            this.normalizeComparisonValue(item),
          ]
        )
      );
    }
    return value;
  }

  private normalizeQuillValue(value: string): string {
    try {
      const delta = JSON.parse(value) as {
        ops?: Array<{
          insert?: unknown;
          attributes?: Record<string, unknown>;
        }>;
      };

      if (!Array.isArray(delta.ops) || delta.ops.length === 0) {
        return value;
      }

      const ops = delta.ops.map(operation => ({ ...operation }));
      const lastOperation = ops[ops.length - 1];
      if (
        typeof lastOperation?.insert === 'string' &&
        !lastOperation.attributes
      ) {
        lastOperation.insert = lastOperation.insert.replace(/\n$/, '');
        if (lastOperation.insert === '') {
          ops.pop();
        }
      }

      return JSON.stringify({ ...delta, ops });
    } catch {
      return value;
    }
  }
}
